import { isUsableUrl, sanitizeHtml } from "@1-now/booking-core";
import { CSSProperties, FC, useState } from "react";
import { useData } from "data/data-provider";
import { sectionPaddingY } from "utils/sectionSpacing";
import DbIcon from "designs/DesignB/components/icons";
import { whyColumns, whyIcon } from "./whyIcons";

const RICH =
  "[&_p]:mb-2 last:[&_p]:mb-0 [&_ul]:list-disc [&_ul]:ps-5 [&_ol]:list-decimal [&_ol]:ps-5 [&_li]:mb-1 " +
  "[&_strong]:font-semibold [&_em]:italic [&_u]:underline [&_a]:underline";

/**
 * The icon in a point's tile: the picture the tenant uploaded for it when there is one that
 * loads, else a line icon from Design B's set picked by what the point says (whyIcons.ts).
 */
const WhyIcon: FC<{ feature: any; index: number; className?: string }> = ({ feature, index, className = "" }) => {
  const [failed, setFailed] = useState(false);
  const own = isUsableUrl(feature?.image_url) && !failed;
  return (
    <div className={`db-why__icon ${className}`}>
      {own ? (
        <img
          src={feature.image_url}
          alt=""
          aria-hidden="true"
          className="h-7 w-7 object-contain"
          onError={() => setFailed(true)}
        />
      ) : (
        <DbIcon name={whyIcon(feature?.heading, index)} className="h-6 w-6" />
      )}
    </div>
  );
};

/**
 * Why choose us (1NOW-11175): a quiet tinted section, the points as hairline cards (after
 * kayak.com's "What KAYAK brings to the table") and no half-empty row (the last row is centred).
 * A point keeps the icon the tenant uploaded for it; one without an icon (or whose icon will not
 * load) gets a line icon from Design B's set.
 */
const WhyChooseUS = () => {
  const { whyUs } = useData();

  const hasHeader = Boolean(whyUs?.heading || whyUs?.description);
  const keypoints: any[] = whyUs?.keypoints ?? [];

  // 1NOW layout variant chosen in the FMS (Section.layout_variant):
  //   "card_grid" → the card grid (default)
  //   "icon_list" → vertical list with icon-left, copy-right per row
  const layoutVariant: "card_grid" | "icon_list" =
    whyUs?.layout_variant === "icon_list" ? "icon_list" : "card_grid";

  return (
    <section
      className={`db-why ${sectionPaddingY(whyUs?.vertical_spacing, "py-12 md:py-16")}`}
    >
      <div className="container">
        {/* Section header */}
        {hasHeader && (
          <div className="mb-8 sm:mb-10">
            <div className="db-eyebrow mb-2">Why choose us</div>
            {whyUs?.heading && (
              <h2
                className="db-title [&_p]:m-0 [&_p]:inline"
                dangerouslySetInnerHTML={{ __html: sanitizeHtml(whyUs.heading) }}
              />
            )}
            {whyUs?.description && (
              <div
                className={`db-sub ${RICH}`}
                dangerouslySetInnerHTML={{ __html: sanitizeHtml(whyUs.description) }}
              />
            )}
          </div>
        )}

        {layoutVariant === "card_grid" && keypoints.length > 0 && (
          <div
            className="db-why__grid"
            style={{ ["--cols" as any]: whyColumns(keypoints.length) } as CSSProperties}
          >
            {keypoints.map((feature: any, index: number) => (
              <article key={index} className="db-why__card">
                <WhyIcon feature={feature} index={index} />
                <h3
                  className="db-why__h [&_p]:m-0 [&_strong]:font-extrabold [&_em]:italic [&_u]:underline"
                  dangerouslySetInnerHTML={{ __html: sanitizeHtml(feature?.heading ?? "") }}
                />
                <div
                  className={`db-why__p ${RICH}`}
                  dangerouslySetInnerHTML={{ __html: sanitizeHtml(feature?.description ?? "") }}
                />
              </article>
            ))}
          </div>
        )}

        {/* Icon list layout — one column, each row an icon on the left and the heading and
            description on the right. */}
        {layoutVariant === "icon_list" && keypoints.length > 0 && (
          <div className="flex w-full flex-col gap-3">
            {keypoints.map((feature: any, index: number) => (
              <div key={index} className="db-why__card flex items-start gap-5">
                <WhyIcon feature={feature} index={index} className="!mb-0" />
                <div className="min-w-0 flex-1 max-w-4xl">
                  <h3
                    className="db-why__h [&_p]:m-0 [&_strong]:font-extrabold [&_em]:italic [&_u]:underline"
                    dangerouslySetInnerHTML={{ __html: sanitizeHtml(feature?.heading ?? "") }}
                  />
                  <div
                    className={`db-why__p ${RICH}`}
                    dangerouslySetInnerHTML={{ __html: sanitizeHtml(feature?.description ?? "") }}
                  />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default WhyChooseUS;
